// Status vocabulary, mirroring awards-service's
// internal/services/membership/transitions.go. Reconcile by hand: the upstream
// @tempmee/awards package is ESM-only with a hard zod@^4 peer dep, so taking it
// as a dependency would drag zod into this repo for a labels map.

export const CYCLE_STATUSES = ['pending', 'active', 'complete'];

export const CYCLE_STATUS_LABELS = {
  pending: 'Pending',
  active: 'Active',
  complete: 'Complete',
};

export const MEMBERSHIP_STATUSES = [
  'nominee',
  'applicant',
  'winner',
  'not-selected',
];

/**
 * Not a stored status — it is the absence of a membership row. Selecting it
 * removes the membership (a formal un-entry: the user leaves the cycle's user
 * list), and selecting an entered status from it creates one (a formal entry).
 * The matrix gates both directions; a winner can never be removed here.
 */
export const NOT_ENTERED = 'not-entered';

export const MEMBERSHIP_STATUS_LABELS = {
  nominee: 'Nominee',
  applicant: 'Applicant',
  winner: 'Winner',
  'not-selected': 'Not Selected',
  'not-entered': 'Not Entered',
};

/** DNT and HYG map to admin profile routes; ADM has no profile page. */
export const USER_TYPE_TO_PROFILE = {
  DNT: 'office',
  HYG: 'professional',
};

/**
 * The server rejects roster-mutating edits — Upload users and per-member
 * status changes — once a cycle is complete. One predicate so the cycle
 * roster and the profile Promotion tab can never disagree about the same
 * cycle's editability.
 */
export const isCycleRosterMutable = (cycleStatus) => cycleStatus !== 'complete';

const WINNER_IS_TERMINAL = 'Winner is final. Use CSV upload to correct.';

// Mirrors legalTransitions in awards-service
// internal/services/membership/transitions.go. Winner is reachable only
// through the funnel (nominee -> applicant -> winner) and has no outbound
// edges: it is published to the marketplace, so corrections stay CSV-only.
// not-entered -> applicant exists for the server's re-add repair, which can
// restore applicant on a member whose application survived an earlier removal.
export const LEGAL_TRANSITIONS = {
  [NOT_ENTERED]: ['nominee', 'applicant'],
  nominee: ['applicant', 'not-selected', NOT_ENTERED],
  applicant: ['winner', 'not-selected', NOT_ENTERED],
  'not-selected': ['nominee', NOT_ENTERED],
  winner: [],
};

const OPTION_ORDER = [...MEMBERSHIP_STATUSES, NOT_ENTERED];

/**
 * Every status the select should render, in a fixed order, each carrying
 * whether it is selectable and why not. Returning the full set (rather than
 * filtering) lets the UI grey illegal targets with an explanation instead of
 * silently hiding them.
 */
export const getStatusOptions = (currentStatus) => {
  const legal = LEGAL_TRANSITIONS[currentStatus] ?? [];
  const currentLabel = MEMBERSHIP_STATUS_LABELS[currentStatus] ?? currentStatus;

  return OPTION_ORDER.map((value) => {
    const label = MEMBERSHIP_STATUS_LABELS[value];

    if (value === currentStatus) {
      return { value, label, disabled: true };
    }
    if (legal.includes(value)) {
      return { value, label, disabled: false };
    }

    return {
      value,
      label,
      disabled: true,
      disabledReason:
        currentStatus === 'winner'
          ? WINNER_IS_TERMINAL
          : `Cannot change from ${currentLabel} to ${label}.`,
    };
  });
};
